import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { FormControl, FormField, FormItem } from '@/components/ui/form'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import useMobileMediaQuery from '@/hooks/useMobileMediaQuery'
import { cn } from '@/lib/utils'
import type { customizeReportSchemaType } from '@/utils/schemas'
import { customizeReportSchema } from '@/utils/schemas'

const REPORT_LANGUAGES = [
  {
    code: 'en',
    labelKey: 'dashboard.runReport.submitReport.languageEnglish',
  },
  {
    code: 'es',
    labelKey: 'dashboard.runReport.submitReport.languageSpanish',
  },
] as const

const REPORT_CONTENT_TOGGLES = [
  {
    key: 'pdfCoreFindings',
    titleKey: 'dashboard.newSearch.reportSetup.coreFinding',
    descriptionKey: 'dashboard.newSearch.reportSetup.coreFindingDescription',
  },
  {
    key: 'pdfSocialMediaAccounts',
    titleKey: 'dashboard.newSearch.reportSetup.socialMediaAccounts',
    descriptionKey:
      'dashboard.newSearch.reportSetup.socialMediaAccountsDescription',
  },
  {
    key: 'pdfSentimentAnalysis',
    titleKey: 'dashboard.newSearch.reportSetup.sentimentAnalysis',
    descriptionKey:
      'dashboard.newSearch.reportSetup.sentimentAnalysisDescription',
  },
  {
    key: 'pdfFlaggedBehaviors',
    titleKey: 'dashboard.newSearch.reportSetup.flaggedBehaviors',
    descriptionKey:
      'dashboard.newSearch.reportSetup.flaggedBehaviorsDescription',
  },
  {
    key: 'pdfPositivePosts',
    titleKey: 'dashboard.newSearch.reportSetup.positivePosts',
    descriptionKey: 'dashboard.newSearch.reportSetup.positivePostsDescription',
  },
  {
    key: 'pdfNeutralPosts',
    titleKey: 'dashboard.newSearch.reportSetup.moderatePosts',
    descriptionKey: 'dashboard.newSearch.reportSetup.moderatePostsDescription',
  },
  {
    key: 'pdfNegativePosts',
    titleKey: 'dashboard.newSearch.reportSetup.negativePosts',
    descriptionKey: 'dashboard.newSearch.reportSetup.negativePostsDescription',
  },
  {
    key: 'pdfWebSearchPosts',
    titleKey: 'dashboard.newSearch.reportSetup.webNewsPosts',
    descriptionKey: 'dashboard.newSearch.reportSetup.webNewsPostsDescription',
  },
  {
    key: 'pdfPersonalityAndSkills',
    titleKey: 'dashboard.newSearch.reportSetup.personalityAndSkills',
    descriptionKey:
      'dashboard.newSearch.reportSetup.personalityAndSkillsDescription',
  },
  {
    key: 'pdfEducationAndCareer',
    titleKey: 'dashboard.newSearch.reportSetup.educationAndCareer',
    descriptionKey:
      'dashboard.newSearch.reportSetup.educationAndCareerDescription',
  },
  {
    key: 'pdfDigitalFootprint',
    titleKey: 'dashboard.newSearch.reportSetup.digitalFootprint',
    descriptionKey:
      'dashboard.newSearch.reportSetup.digitalFootprintDescription',
  },
  {
    key: 'pdfContentRadar',
    titleKey: 'dashboard.newSearch.reportSetup.contentRadar',
    descriptionKey: 'dashboard.newSearch.reportSetup.contentRadarDescription',
  },
  {
    key: 'pdfUnverifiedDigitalFootprint',
    titleKey: 'dashboard.newSearch.reportSetup.unverifiedDigitalFootprint',
    descriptionKey:
      'dashboard.newSearch.reportSetup.unverifiedDigitalFootprintDescription',
  },
] as const satisfies ReadonlyArray<{
  key: keyof customizeReportSchemaType
  titleKey: string
  descriptionKey: string
}>

interface CustomizeReportSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  value: customizeReportSchemaType
  onSave: (next: customizeReportSchemaType) => void
  hideLanguageSection?: boolean
}

export const CustomizeReportSheet = ({
  open,
  onOpenChange,
  value,
  onSave,
  hideLanguageSection = false,
}: CustomizeReportSheetProps) => {
  const { t } = useTranslation()
  const isMobile = useMobileMediaQuery()

  const form = useForm<customizeReportSchemaType>({
    resolver: zodResolver(customizeReportSchema),
    defaultValues: value,
    mode: 'onChange',
  })

  const { handleSubmit, reset, watch, control } = form

  useEffect(() => {
    if (open) reset(value)
  }, [open, value, reset])

  const currentLanguage = watch('pdfLanguage')

  const handleCancel = () => onOpenChange(false)
  const handleConfirm = handleSubmit((data) => {
    onSave(data)
    onOpenChange(false)
  })

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isMobile ? 'bottom' : 'right'}
        className="flex flex-col gap-4 max-md:h-[85vh] max-md:rounded-t-2xl sm:max-w-md"
      >
        <SheetHeader className="text-center sm:text-center">
          <SheetTitle>
            {t('dashboard.runReport.submitReport.customizeSheetTitle')}
          </SheetTitle>
        </SheetHeader>

        <FormProvider {...form}>
          <form
            onSubmit={handleConfirm}
            className="flex flex-1 min-h-0 flex-col gap-5 overflow-y-auto"
          >
            {!hideLanguageSection && (
              <div className="flex flex-col gap-3">
                <p className="text-sm font-medium text-neutralColor60">
                  {t(
                    'dashboard.runReport.submitReport.customizeSheetLanguageSection',
                  )}
                </p>
                <FormField
                  control={control}
                  name="pdfLanguage"
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-3">
                      {REPORT_LANGUAGES.map(({ code, labelKey }) => {
                        const isSelected = currentLanguage === code
                        return (
                          <label
                            key={code}
                            htmlFor={`lang-${code}`}
                            className="flex cursor-pointer items-center justify-between gap-3 rounded-[10px] border border-neutralColor5 bg-baseColorHighlight p-3 transition-colors hover:bg-baseColor"
                          >
                            <span className="flex-1 text-sm text-neutralColor">
                              {t(labelKey)}
                            </span>
                            <FormControl>
                              <Checkbox
                                id={`lang-${code}`}
                                checked={isSelected}
                                onCheckedChange={() => field.onChange(code)}
                              />
                            </FormControl>
                          </label>
                        )
                      })}
                    </FormItem>
                  )}
                />
              </div>
            )}

            <div className="flex flex-col gap-3">
              <p className="text-sm font-medium text-neutralColor60">
                {t(
                  'dashboard.runReport.submitReport.customizeSheetReportContentSection',
                )}
              </p>
              <div className="flex flex-col gap-3">
                {REPORT_CONTENT_TOGGLES.map(
                  ({ key, titleKey, descriptionKey }) => (
                    <FormField
                      key={key}
                      control={control}
                      name={key}
                      render={({ field }) => (
                        <FormItem>
                          <label
                            htmlFor={`toggle-${key}`}
                            className={cn(
                              'flex cursor-pointer items-start justify-between gap-3 rounded-[10px] border bg-baseColorHighlight p-3 transition-colors',
                              field.value
                                ? 'border-primaryColor'
                                : 'border-neutralColor5 hover:bg-baseColor',
                            )}
                          >
                            <div className="flex flex-1 flex-col gap-1.5">
                              <span className="text-sm font-medium text-neutralColor">
                                {t(titleKey)}
                              </span>
                              <span className="text-xs text-neutralColor80">
                                {t(descriptionKey)}
                              </span>
                            </div>
                            <FormControl>
                              <Checkbox
                                id={`toggle-${key}`}
                                checked={field.value}
                                onCheckedChange={(checked) =>
                                  field.onChange(checked === true)
                                }
                                className="mt-0.5"
                              />
                            </FormControl>
                          </label>
                        </FormItem>
                      )}
                    />
                  ),
                )}
              </div>
            </div>
          </form>
        </FormProvider>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={handleCancel}>
            {t('dashboard.runReport.submitReport.cancel')}
          </Button>
          <Button type="button" onClick={handleConfirm}>
            {t('dashboard.runReport.submitReport.confirm')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
